import React from 'react'
import { observer } from 'mobx-react'
import { autorun } from 'mobx'
import classNames from 'classnames'
import { IntlProvider, addLocaleData } from 'react-intl'
import { autobind } from 'core-decorators'
import en from 'react-intl/locale-data/en'
import de from 'react-intl/locale-data/de'

import Sidebar from './Sidebar/Sidebar'
import Header from './Header/Header'
import Login from './Login/Login'
import WarningDialog from './modals/WarningDialog'

import config from '../config/index'
import GenevaWrapper from '../shared/custom/geneva-wrapper'
import ContentLoadingBox from '../shared/components/ContentLoadingBox'
import enlocaleData from '../static/intl/en.json'
import delocaleData from '../static/intl/de.json'
import { deepGet } from '../shared/obj'
import { IUserResponse, IReviewModel } from '../models'
import { IApplicationStore, IReviewStore } from '../stores'

import './App.scss'

const localeData = { en: { ...enlocaleData }, de: { ...delocaleData } }

addLocaleData([...en, ...de])

// Typescript specific cast
const globalAny:any = global

interface IAppProps {
  applicationStore: IApplicationStore
  children: any
  location: { query: { token: number }}
  params: { id: string }
  reviewStore: IReviewStore
  route: any
}
interface IState {
  user: any,
  validUser: boolean
  invalidURL: boolean
  loggedIn: boolean
  errorType: string
  appLanguage: string
  messages: Object,
  iframeReloadKey: number,
  showWarningModal: boolean,
}

@observer
class App extends React.Component<IAppProps> {
  state: IState
  handler

  constructor(props: IAppProps) {
    super(props)
    const { applicationStore } = props

    this.state = {
      user: {},
      validUser: false,
      invalidURL: false,
      loggedIn: false,
      errorType: '',
      appLanguage: applicationStore.languageWithoutRegionCode,
      messages: localeData[applicationStore.languageWithoutRegionCode] || localeData.en,
      iframeReloadKey: 0,
      showWarningModal: false,
    }

    // Set initial values to Stores:
    applicationStore.setPageId(deepGet(props, 'params.id'))
    applicationStore.token = deepGet(props, 'location.query.token')

     // Build out the iframe src
    applicationStore.iframeUrl = `${config.pathPreview}${applicationStore.pageId}/preview`

    // See if a password is required
    GenevaWrapper.get(
      `review/validate/${applicationStore.token}`,
      {
        successCallback: (body: IUserResponse) => {
          applicationStore.setUser(body)
          if (applicationStore.pageId === applicationStore.tokenPageId) {
            applicationStore.setLoginStatus('true')
            this.setState({
              loggedIn: true
            })
            applicationStore.setRoom()
          }
          else {
            applicationStore.setLoginStatus('false')
            this.setState({
              invalidURL: true,
              errorType: 'url'
            })
          }
        },
        errorCallback: (res: IUserResponse) => {
          // set login so the UI knows that all responses have returned
          applicationStore.setLoginStatus('false')

          // needs a login
          if (res && res.data) {
            if (res.data) {
              applicationStore.setUser(res)
            }
          }
          else {
            // invalid URL
            this.setState({
              invalidURL: true,
              errorType: res && res.status_code === 422 ? 'url2' : 'other'
            })

          }
        }

      }
    )
  }

  componentDidMount() {
    // handler for iFrame that catches all link clicks from the iFrame
    window.addEventListener("message", (e) => {
      if (e && e.data === 'link') {
        this.setState({
          showWarningModal: true
        })
      }
    }, false);

    this.handler = autorun(() => {
      if (this.props.applicationStore.requestIframeUpdate) {
        this.updateIframe()
      }
    })
  }

  @autobind
  handleUpdateLoggedIn(loggedIn: Boolean) {
    this.setState({
      loggedIn
    })
  }

  // Searches for a sepecific parameter in the URL
  findParameter(parameterName: string) {
    let existance = false

    location.search
      .substr(1)
      .split('&')
      .forEach((item) => {
        // only need it once in string
        existance = (item === parameterName || existance)
      })
    return existance
  }

  @autobind
  loadReview() {
    const { reviewStore, applicationStore } = this.props

    if (!reviewStore.current && !this.state.invalidURL && this.state.loggedIn) {
      GenevaWrapper.get(
        `review/${applicationStore.pageId}`,
        {
          token: applicationStore.token,
          successCallback: (body: { data: IReviewModel }) => {
            reviewStore.createAndSetCurrent(body.data)
            reviewStore.setHiddenMessageCount(body.data)
          },
          errorCallback: (res: IUserResponse) => {
            console.warn(res)
          }
        }
      )
    }
  }

  updateIframe() {
    this.setState({
      iframeReloadKey: this.state.iframeReloadKey + 1
    })
    this.props.applicationStore.requestIframeUpdate = false
    this.props.applicationStore.pageChanged = false
  }

  @autobind
  closeWarningDialog(): void {
    this.setState({
      showWarningModal: false
    })
  }

  renderInvalidUser() {
    return (
      <Login
        applicationStore={this.props.applicationStore}
        handleUpdateLoggedIn={this.handleUpdateLoggedIn}
        invalidURL={this.state.invalidURL}
        errorType={this.state.errorType}
      />
    )
  }


  renderReviewCenterBody() {
    const { applicationStore, reviewStore } = this.props

    const size = {
      width: '100%',
      height: 'calc(100vh - 48px)'
    }

    return (
      <div>
        <Header
          applicationStore={applicationStore}
          reviewStore={reviewStore}
        />
        <div className="app-body">
          <div className="app-heading">

            <iframe
              title="Preview Iframe"
              id="iframe"
              key={this.state.iframeReloadKey}
              ref={(ref) => {
                globalAny.refIFrame = ref
              }}
              style={{
                width: size.width,
                height: size.height
              }}
              src={`${applicationStore.iframeUrl}?token=${applicationStore.token}`}
            />
            <div className="loading-body">
              <ContentLoadingBox spinner />
            </div>
            <div className={classNames('sidebar', { hidden: !applicationStore.showSidebar })}>
              <Sidebar
                reviewStore={this.props.reviewStore}
                applicationStore={this.props.applicationStore}
              >
              </Sidebar>
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { applicationStore } = this.props
    // sits here until all conditions for a load are possible
    this.loadReview()

    if (this.state.appLanguage !== applicationStore.language) {
      this.setState({
        appLanguage: applicationStore.language,
        messages: localeData[applicationStore.language] || localeData.en
      })
    }
    return (
      <IntlProvider
        locale={applicationStore.language}
        messages={this.state.messages}
      >
        <div className="container">
          {!this.state.loggedIn
            ? this.renderInvalidUser()
            : this.renderReviewCenterBody()
          }
          {this.state.showWarningModal
            ? <WarningDialog
              {...this.props}
              show={this.state.showWarningModal}
              handleClose={this.closeWarningDialog}
            />
            : null
          }
        </div>
      </IntlProvider>
    )
  }
}

export default App
